import { IEditorConfig, PosterType } from '@/version-types';
import { Events } from '../events';
import { getEditorState } from '../states/editor-state';
import { getIdentityState } from '../states/identity-state';
import { getLoadingState } from '../states/loading-state';

/** 核心基础类 */
export class CoreBase {
    /** 主题变化事件 */
    private themeEventName = 'gs-theme-change';
    /** 海报类型变化事件 */
    private posterTypeEventName = 'gs-poster-type-change';

    /** 配置 */
    protected config: IEditorConfig = {
        // ATTENTION: 这里的 padding 仅用于初始化的时候设置舞台边距, 后续以 stage.getViewPortPadding() 为准, 不要再使用这里的 padding 了
        padding: 0,
        PUBLIC_URL: '',
    };

    /** 事件总线 */
    public events: Events;
    /** 编辑器唯一标识 */
    public identity: string;

    /** 唯一标识 */
    public uid: number = 0;

    constructor(identity: string) {
        this.identity = identity;
        this.events = new Events();
    }

    /** 设置唯一标识 */
    setUid(uid: number) {
        this.uid = uid;
    }

    /** 获取唯一标识 */
    getUid() {
        return this.uid;
    }

    destroy() {
        getEditorState(this.identity).destroy();
        getLoadingState(this.identity).destroy();
    }

    /** 初始化 */
    init() {
        getIdentityState(this.identity).destroy();
    }

    /** 获取版本 */
    getVersion() {
        return getIdentityState(this.identity).version;
    }

    /** 增加版本 */
    incrVersion() {
        return getIdentityState(this.identity).incr();
    }

    /** 配置 *************************************************/

    /** 获取配置 */
    getConfig() {
        return this.config;
    }

    /** 设置配置 */
    setConfig(config: IEditorConfig) {
        this.config = {
            ...this.config,
            ...config,
        };
    }

    /** 主题 *************************************************/

    /** 获取主题 */
    getTheme() {
        return getEditorState(this.identity).theme;
    }

    /** 设置主题 */
    setTheme(theme: string) {
        if (this.getTheme() !== theme) {
            getEditorState(this.identity).setTheme(theme);
            this.events.emit(this.themeEventName, theme);
        }
    }

    /** 监听主题变化 */
    onTheme(fn: (theme: string) => void, weight = 0) {
        return this.events.on(this.themeEventName, fn, weight);
    }

    /** 海报类型 *************************************************/

    /** 获取海报类型 */
    getPosterType() {
        return getEditorState(this.identity).posterType;
    }

    /** 设置海报类型 */
    setPosterType(posterType: PosterType) {
        return getEditorState(this.identity).setPosterType(posterType);
    }

    /** 监听海报类型变化 */
    onPosterType(fn: (posterType: PosterType) => void, weight = 0) {
        return this.events.on(this.posterTypeEventName, fn, weight);
    }

    /** 上下文菜单 *************************************************/

    /**
     * 是否显示上下文菜单
     * 从旧版 isContextMenu 迁移
     */
    getShowContextMenu() {
        return getEditorState(this.identity).showContextMenu;
    }

    /**
     * 设置是否显示上下文菜单
     * 从旧版 setIsContextMenu 迁移
     */
    setShowContextMenu(showContextMenu: boolean) {
        return getEditorState(this.identity).setShowContextMenu(showContextMenu);
    }

    /** 图层加载 *************************************************/

    /**
     * 检查图层是否正在加载
     *
     * @param layerId - 图层 ID
     */
    checkIsLoading(layerId: string) {
        return getLoadingState(this.identity).checkIsLoading(layerId);
    }

    /**
     * 设置加载图层
     *
     * @param layerId - 图层 ID
     * @param extraInfo - 可选；额外信息
     * @returns
     */
    setLoading(layerId: string, extraInfo?: string) {
        return getLoadingState(this.identity).addLoading(layerId, extraInfo);
    }

    /**
     * 移除加载图层
     *
     * @param layerId - 图层 ID
     * @param extraInfo - 可选；额外信息
     */
    removeLoading(layerId: string, extraInfo?: string) {
        return getLoadingState(this.identity).removeLoading(layerId, extraInfo);
    }

    /** 图层闪烁 *************************************************/

    /** 闪烁时长 */
    private duration = 1200 * 3;

    /**
     * 添加闪烁
     *
     * @param layerId - 图层 ID
     * @param duration - 闪烁时长
     */
    addShine(layerId: string, duration = this.duration) {
        this.events.emitLayerShine(layerId, duration);
    }

    /**
     * 移除闪烁
     *
     * @param layerId - 图层 ID
     */
    removeShine(layerId: string) {
        this.events.emitLayerShineHide(layerId);
    }
}
